/**
 * my_page_v2.js — DAS-107 Phase 05: MyPage V2 시안 V1 1:1 재정합
 *
 * 원본: ClaudeDesignDashboard/pages/mypage.jsx::MyPageDesktopV1 / MyPageMobileV1
 * UI: 전략별 그룹 테이블 (시안 V1 — 현행 IS 리프레시)
 *     데스크톱 — 전략 섹션 헤더 (⌘ 전략:) + 테이블 행
 *     모바일   — 전략별 카드 스택 (compact, 수정/삭제 없음 — 시안 V1 정합)
 *
 * 데이터: services/apiClient (/api/exchange_key_pair/{username})
 * APIKeyEditable: hideListRender prop 으로 CRUD 모달만 사용 (목록 렌더 비활성)
 *   - ref.openAddModal() / ref.startEdit(row) / ref.confirmDelete(id) 위임
 *   - revealedSecrets: APIKeyEditable.fetchSecrets polling (v3 검증된 패턴)
 *
 * 시안 V1 정합 포인트:
 *   - 카드 헤더: "보유 Key Pair" + 부제 "전략 단위로 거래소 키를 짝지어 관리하세요."
 *   - 전략 구분 행: prefix `⌘ 전략:` + surface-card-alt 배경
 *   - 카드 푸터 힌트: alert 아이콘 + "수정 시 시크릿 … 기존 값이 유지됩니다."
 *   - 추가 버튼: Btn variant="primary" size="sm" icon="plus"
 *   - 모바일: MobileV1 — 수정/삭제 버튼 없음, FIAT KEY / RISKY KEY 2칼럼 그리드
 *
 * BE gap G5:
 *   - status 필드 없음 → StatusDot tone="gain" + TODO(DAS-65-G5)
 *   - last_used_at 없음 → '—' 표시 + TODO(DAS-65-G5)
 *
 * silver 토큰 전용. JetBrains Mono 유지. raw hex 금지.
 * resize 리스너. HexLogo 브랜드 통일.
 */

import React from 'react';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import APIKeyEditable from '../my_page/api_key_editable';
import { requireLoginOrThrow, getStoredUsername } from '../../utils/auth';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import apiClient from '../../services/apiClient';
import { StatusDot, ExchangeChip, KpiLabel, V2Icon, V2Badge, Btn } from '../common/claude_primitives';

/* ── 마스킹 키 셀 ───────────────────────────────────────── */
function KeyPill({ val }) {
    return (
        <div style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            padding: '6px 10px',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 7,
            letterSpacing: '0.05em',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--color-content-secondary)',
            maxWidth: '100%',
            overflow: 'hidden',
        }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {val || '—'}
            </span>
            <V2Icon name="copy" size={11} />
        </div>
    );
}

/* ── 보안 권고 배너 ──────────────────────────────────────── */
function SecurityNotice() {
    return (
        <div
            className="rounded-xl border bg-warning-subtle"
            style={{
                padding: 16,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                borderColor: 'color-mix(in oklch, var(--color-warning) 40%, var(--color-surface-border))',
            }}
        >
            <V2Icon name="alert" size={18} style={{ color: 'var(--color-warning)', flexShrink: 0, marginTop: 1 }} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--color-content)' }}>
                    보안 권장 사항
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--color-content-secondary)' }}>
                    수정 시 시크릿(API Key, Secret Key) 필드를 비워두면 기존 값이 유지됩니다.
                    API Key는 IP 화이트리스트를 설정하고 출금 권한은 비활성화한 상태로 사용하시길 권장합니다.
                </div>
            </div>
        </div>
    );
}

/* ── 테이블 행 액션 버튼 (시안 V1 pill 패턴) ─────────────── */
function RowActions({ onEdit, onShowSecret, onDelete }) {
    return (
        <div style={{ display: 'flex', gap: 4 }}>
            <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                aria-label="수정"
            >
                <V2Icon name="edit" size={10} />
                수정
            </button>
            <button
                type="button"
                onClick={onShowSecret}
                className="inline-flex items-center gap-1 rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                aria-label="키 보기"
            >
                <V2Icon name="eye" size={10} />
                보기
            </button>
            <button
                type="button"
                onClick={onDelete}
                className="inline-flex items-center gap-1 rounded-lg border border-loss/30 bg-surface-card px-2 py-1.5 text-xs text-loss hover:bg-loss-subtle transition-colors"
                aria-label="삭제"
            >
                <V2Icon name="trash" size={10} />
                삭제
            </button>
        </div>
    );
}

/* ── 데스크톱: 전략 그룹 테이블 (시안 MyPageDesktopV1) ──── */
function MyPageDesktopV1({ keyPairs, isLoading, onOpenAddModal, onEdit, onDelete, onShowSecret, revealedSecrets, collapsed, onCollapse }) {
    /* 전략별 그룹화 */
    const grouped = {};
    (keyPairs || []).forEach((row) => {
        const sn = row.strategy_name || 'default';
        if (!grouped[sn]) grouped[sn] = [];
        grouped[sn].push(row);
    });
    const strategyNames = Object.keys(grouped).sort();

    return (
        <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
            <NavBar collapsed={collapsed} onCollapse={onCollapse} />
            <main
                className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'}`}
                style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
            >
                <MaintenanceBanner />
                <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                    {/* 페이지 헤더 — 시안 TopBar crumbs + title */}
                    <div>
                        <div className="text-sm text-content-secondary">내 정보 / API Key 관리</div>
                        <h1 className="mt-1 text-xl font-semibold text-content">마이 페이지</h1>
                    </div>

                    {/* Key Pair 테이블 카드 — 시안 .c 래퍼 */}
                    <div
                        className="rounded-2xl border border-surface-border bg-surface-card shadow-card"
                        style={{ overflow: 'hidden' }}
                    >
                        {/* 카드 헤더 — 시안 .c-head */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px',
                            borderBottom: '1px solid var(--color-surface-border)',
                        }}>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-content)' }}>
                                    보유 Key Pair
                                </div>
                                <div style={{ fontSize: 12, marginTop: 4, color: 'var(--color-content-secondary)' }}>
                                    전략 단위로 거래소 키를 짝지어 관리하세요.
                                    {!isLoading && (keyPairs || []).length > 0 && ` · ${(keyPairs || []).length}개 등록 · ${strategyNames.length}개 전략`}
                                </div>
                            </div>
                            {/* 시안 V1: Btn variant="primary" size="sm" icon="plus" */}
                            <Btn
                                variant="primary"
                                size="sm"
                                icon="plus"
                                onClick={onOpenAddModal}
                                disabled={isLoading}
                                aria-label="새 Key Pair 추가"
                            >
                                새 Key Pair 추가
                            </Btn>
                        </div>

                        {/* 테이블 본문 */}
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <svg className="h-8 w-8 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            </div>
                        ) : (keyPairs || []).length === 0 ? (
                            <div className="py-16 text-center text-sm text-content-muted">
                                등록된 Key Pair가 없습니다.
                            </div>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: 12,
                                    fontFamily: 'JetBrains Mono, monospace',
                                }}>
                                    <thead>
                                        <tr style={{ background: 'var(--color-surface-elevated)' }}>
                                            {['ID', '전략', '현물', 'FIAT API KEY', 'FIAT SECRET', '선물', 'RISKY API KEY', 'RISKY SECRET', ''].map((h) => (
                                                <th
                                                    key={h}
                                                    scope="col"
                                                    style={{
                                                        padding: '10px 12px',
                                                        textAlign: 'left',
                                                        fontSize: 10,
                                                        letterSpacing: '0.12em',
                                                        textTransform: 'uppercase',
                                                        color: 'var(--color-content-muted)',
                                                        fontWeight: 600,
                                                        borderBottom: '1px solid var(--color-surface-border)',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {strategyNames.map((sn) => (
                                            <React.Fragment key={sn}>
                                                {/* 전략 구분 행 — 시안 V1: ⌘ 전략: + surface-card-alt */}
                                                <tr>
                                                    <td
                                                        colSpan={9}
                                                        style={{
                                                            background: 'var(--color-surface-card-alt)',
                                                            padding: '8px 12px',
                                                            borderBottom: '1px solid var(--color-surface-border)',
                                                            borderTop: '1px solid var(--color-surface-border)',
                                                        }}
                                                    >
                                                        <span style={{
                                                            fontSize: 10,
                                                            letterSpacing: '0.12em',
                                                            textTransform: 'uppercase',
                                                            color: 'var(--color-content-muted)',
                                                            fontFamily: 'JetBrains Mono, monospace',
                                                        }}>
                                                            ⌘ 전략:
                                                        </span>
                                                        <span style={{
                                                            marginLeft: 8,
                                                            fontWeight: 600,
                                                            fontSize: 13,
                                                            color: 'var(--color-content)',
                                                            fontFamily: 'inherit',
                                                        }}>
                                                            {sn}
                                                        </span>
                                                    </td>
                                                </tr>

                                                {/* 키페어 데이터 행 */}
                                                {grouped[sn].map((row) => {
                                                    const fiatExchange = row.fiat_exchange || '미설정';
                                                    const riskyExchange = row.risky_exchange || '미설정';
                                                    const rev = revealedSecrets?.[row.id];
                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            style={{
                                                                borderBottom: '1px solid var(--color-surface-border)',
                                                                background: 'var(--color-surface)',
                                                                transition: 'background 0.1s',
                                                            }}
                                                            className="hover:bg-surface-hover"
                                                        >
                                                            <td style={{ padding: '10px 12px', color: 'var(--color-content-muted)', fontSize: 11 }}>
                                                                {row.id}
                                                            </td>
                                                            <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--color-content)', fontFamily: 'inherit' }}>
                                                                {sn}
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                {fiatExchange === '미설정'
                                                                    ? <V2Badge tone="neutral">미설정</V2Badge>
                                                                    : <ExchangeChip name={fiatExchange} />
                                                                }
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                <KeyPill val={rev?.fiat_api_key || row.fiat_api_key} />
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                <KeyPill val={rev?.fiat_secret_key || row.fiat_secret_key} />
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                {riskyExchange === '미설정'
                                                                    ? <V2Badge tone="neutral">미설정</V2Badge>
                                                                    : <ExchangeChip name={riskyExchange} />
                                                                }
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                <KeyPill val={rev?.risky_api_key || row.risky_api_key} />
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                <KeyPill val={rev?.risky_secret_key || row.risky_secret_key} />
                                                            </td>
                                                            <td style={{ padding: '10px 12px' }}>
                                                                <RowActions
                                                                    onEdit={() => onEdit(row)}
                                                                    onShowSecret={() => onShowSecret(row.id)}
                                                                    onDelete={() => onDelete(row.id)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* 카드 외부 푸터 힌트 — 시안 V1 .sec alert 텍스트 */}
                    {!isLoading && (keyPairs || []).length > 0 && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '0 4px',
                            fontSize: 11,
                            color: 'var(--color-content-muted)',
                            fontFamily: 'JetBrains Mono, monospace',
                        }}>
                            <V2Icon name="alert" size={12} />
                            수정 시 시크릿(API Key, Secret Key) 필드를 비워두면 기존 값이 유지됩니다.
                        </div>
                    )}

                    {/* 보안 권고 배너 */}
                    <SecurityNotice />
                </div>
            </main>
            <ScrollToTopButton />
        </div>
    );
}

/* ── 모바일: 전략별 카드 스택 (시안 MyPageMobileV1) ────────
 * 시안: 추가 버튼(primary) → 전략별 라벨 → 카드 (#{id} + 거래소칩 + 상태점)
 *       카드 내부: FIAT KEY / RISKY KEY 2칼럼 그리드
 *       수정/삭제 버튼 없음 (시안 V1 MobileV1 정합)
 * ──────────────────────────────────────────────────────── */
function MyPageMobileV1({ keyPairs, isLoading, onOpenAddModal, collapsed, onCollapse }) {
    const grouped = {};
    (keyPairs || []).forEach((row) => {
        const sn = row.strategy_name || 'default';
        if (!grouped[sn]) grouped[sn] = [];
        grouped[sn].push(row);
    });
    const strategyNames = Object.keys(grouped).sort();

    return (
        <div className="min-h-screen w-full bg-surface text-content">
            <NavBar collapsed={collapsed} onCollapse={onCollapse} />
            <main
                className="w-full pt-12 pb-20"
                style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
            >
                <MaintenanceBanner />
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {/* 페이지 헤더 — 시안 MobileTop title="마이 페이지" */}
                    <div>
                        <div className="text-xs text-content-secondary">내 정보 / API Key 관리</div>
                        <h1 className="mt-1 text-lg font-semibold text-content">마이 페이지</h1>
                    </div>

                    {/* 추가 버튼 — 시안 V1: Btn variant="primary" size="md" icon="plus" */}
                    <Btn
                        variant="primary"
                        size="md"
                        icon="plus"
                        onClick={onOpenAddModal}
                        disabled={isLoading}
                        aria-label="새 Key Pair 추가"
                        style={{ width: '100%', justifyContent: 'center' }}
                    >
                        새 Key Pair 추가
                    </Btn>

                    {/* 전략별 목록 */}
                    {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <svg className="h-6 w-6 animate-spin text-brand-light" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                        </div>
                    ) : (keyPairs || []).length === 0 ? (
                        <div className="py-12 text-center text-sm text-content-muted">
                            등록된 Key Pair가 없습니다.
                        </div>
                    ) : (
                        strategyNames.map((sn) => (
                            <div key={sn}>
                                {/* 전략 레이블 — 시안 V1 MobileV1: mono muted uppercase */}
                                <div style={{
                                    fontSize: 10,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                    color: 'var(--color-content-muted)',
                                    fontFamily: 'JetBrains Mono, monospace',
                                    padding: '4px 2px 8px',
                                }}>
                                    전략 · {sn}
                                </div>

                                {/* 카드 목록 */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {grouped[sn].map((row) => {
                                        const fiatExchange = row.fiat_exchange || '미설정';
                                        const riskyExchange = row.risky_exchange || '미설정';
                                        return (
                                            <div
                                                key={row.id}
                                                className="rounded-xl border border-surface-border bg-surface-card shadow-card"
                                                style={{ padding: 14 }}
                                            >
                                                {/* 카드 헤더: #{id} 거래소→거래소 StatusDot */}
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    marginBottom: 10,
                                                    flexWrap: 'wrap',
                                                }}>
                                                    <span style={{
                                                        fontFamily: 'JetBrains Mono, monospace',
                                                        fontSize: 10,
                                                        color: 'var(--color-content-muted)',
                                                    }}>
                                                        #{row.id}
                                                    </span>
                                                    {fiatExchange === '미설정'
                                                        ? <V2Badge tone="neutral">미설정</V2Badge>
                                                        : <ExchangeChip name={fiatExchange} />
                                                    }
                                                    <V2Icon name="arrowR" size={12} style={{ color: 'var(--color-content-muted)' }} />
                                                    <ExchangeChip name={riskyExchange} />
                                                    {/* TODO(DAS-65-G5): status 필드 없음 — BE 추가 후 실제 상태 표시 */}
                                                    <span style={{ marginLeft: 'auto' }}>
                                                        <StatusDot tone="gain" size={6} />
                                                    </span>
                                                </div>

                                                {/* 키 그리드 — 시안 V1 MobileV1: FIAT KEY / RISKY KEY */}
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                                    <div>
                                                        <KpiLabel>FIAT KEY</KpiLabel>
                                                        <KeyPill val={row.fiat_api_key} />
                                                    </div>
                                                    <div>
                                                        <KpiLabel>RISKY KEY</KpiLabel>
                                                        <KeyPill val={row.risky_api_key} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}

                    <SecurityNotice />
                </div>
            </main>
            <ScrollToTopButton />
        </div>
    );
}

/* ── 메인 export: MyPageV2 ──────────────────────────────── */
class MyPageV2 extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            listKeyPair: [],
            isLoading: true,
            revealedSecrets: {},
            isMobile: isMobileViewport(),
        };
        this.apiKeyRef = React.createRef();
        this._onResize = this._onResize.bind(this);
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.loadKeyPairs();
        window.addEventListener('resize', this._onResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onResize);
    }

    loadKeyPairs = () => {
        const username = getStoredUsername();
        if (!username) {
            this.setState({ isLoading: false });
            return;
        }
        this.setState({ isLoading: true });
        apiClient
            .get(`/api/exchange_key_pair/${username}`)
            .then((res) => {
                this.setState({ listKeyPair: Array.isArray(res.data) ? res.data : [], isLoading: false });
            })
            .catch(() => {
                this.setState({ isLoading: false });
            });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    handleOpenAddModal = () => {
        if (this.apiKeyRef.current) this.apiKeyRef.current.openAddModal();
    };

    handleEdit = (row) => {
        if (this.apiKeyRef.current) this.apiKeyRef.current.startEdit(row);
    };

    handleDelete = (rowId) => {
        if (this.apiKeyRef.current) this.apiKeyRef.current.confirmDelete(rowId);
    };

    handleShowSecret = (rowId) => {
        if (!this.apiKeyRef.current) return;
        /* 이미 공개 상태면 토글로 마스킹 복귀 */
        if (this.state.revealedSecrets[rowId]) {
            this.setState((prev) => {
                const next = { ...prev.revealedSecrets };
                delete next[rowId];
                return { revealedSecrets: next };
            });
            return;
        }
        this.apiKeyRef.current.fetchSecrets(rowId);
        /* fetchSecrets 비동기 완료 후 polling 으로 읽어옴 (v3 검증 패턴) */
        const pollSecrets = (retries = 20) => {
            const inst = this.apiKeyRef.current;
            if (!inst) return;
            const secretData = inst.state.secretData;
            if (secretData && secretData[rowId]) {
                this.setState((prev) => ({
                    revealedSecrets: { ...prev.revealedSecrets, [rowId]: secretData[rowId] },
                }));
                return;
            }
            if (retries <= 0) return;
            setTimeout(() => pollSecrets(retries - 1), 100);
        };
        pollSecrets();
    };

    render() {
        const { collapsed, listKeyPair, isLoading, isMobile, revealedSecrets } = this.state;

        const sharedProps = {
            keyPairs: listKeyPair,
            isLoading,
            onOpenAddModal: this.handleOpenAddModal,
            onEdit: this.handleEdit,
            onDelete: this.handleDelete,
            onShowSecret: this.handleShowSecret,
            revealedSecrets,
            collapsed,
            onCollapse: this.onCollapse,
        };

        return (
            <>
                {isMobile
                    ? <MyPageMobileV1 {...sharedProps} />
                    : <MyPageDesktopV1 {...sharedProps} />
                }
                {/* APIKeyEditable: hideListRender=true 로 CRUD 모달만 사용, 목록 표시는 V2가 담당 */}
                <APIKeyEditable
                    ref={this.apiKeyRef}
                    hideListRender
                    onDataChange={this.loadKeyPairs}
                />
            </>
        );
    }
}

export default MyPageV2;
